import React from 'react';

import {
  fallbackText,
  HelpTypes,
  IAdminPharmacistDetails,
  IAdminProfessionalDetails,
  ServiceTypes,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { goToFallback } from '@pharmaplan/common/helpers/utils';
import strings from '../../../../../localization';

import {
  convertDecimal,
  formatLeadSource,
  handleBooleanValue,
  handleSuffix,
  handleValue,
} from '../../Pharmacy/PharmacyViewProfile/helper';
import {
  OutcomeModalTypes,
  TypeOfSoftwareExpertise,
  UserLabels,
  preferredDaysMap,
} from '../../../../../helpers/Constants';
import SoftwareExpertise from '../../../../SelfService/SoftwareExpertise';
import { setDialog } from '../../../../../reducers/dialogReducer';
import OutcomeModal from '../../../../Modals/OutcomeModal';
import { store } from '../../../../../store';
import { languageLevelMap } from '../../../../Profile/ProfileForm/Forms';
import { getDayMonthDateYear } from '../../../../Reports/helpers';

const campaignKey = 'campaignKey';
export const professionalSelfServiceKeys = {
  configuration: 'configuration',
  billing: 'billing',
  incorporated: 'incorporated',
  licenseKey: 'licenseNumber',
  graduationYear: 'graduationYear',
  yearsOfExperience: 'yearsOfExperience',
  technicianDiploma: 11,
  technicianInstitute: 21,
  technicianGraduationYear: 31,
};

export const showSuccess = (
  dispatch: typeof store.dispatch,
  message: React.ReactNode,
) => {
  dispatch(
    setDialog({
      heading: {
        title: '',
      },
      Component: (
        <OutcomeModal type={OutcomeModalTypes.justSuccess} message={message} />
      ),
      showCloseButton: true,
    }),
  );
};

export const showError = (dispatch: typeof store.dispatch, message: string) => {
  dispatch(
    setDialog({
      heading: {
        title: '',
      },
      Component: (
        <OutcomeModal type={OutcomeModalTypes.error} message={message} />
      ),
      showCloseButton: true,
    }),
  );
};

export const handlePreferredDays = (preferredWorkingDays: string) =>
  preferredWorkingDays
    ?.split(',')
    .map((item) =>
      preferredDaysMap()[parseInt(item, 10)])
    .join('; ');

interface IPharmacistProfileParams {
  details: IAdminPharmacistDetails | IAdminProfessionalDetails;
  language: string;
  pharmacistCapacity: string;
  successAction: string;
  selectedPreferences: string;
  fromPendingActivations: boolean;
  resumeName: string;
  openResume: () => void;
  openCertificate: (certificateUrl: string) => () => void;
}
export const pharmacistProfileConfig = ({
  details,
  language,
  pharmacistCapacity,
  successAction,
  selectedPreferences,
  fromPendingActivations,
  resumeName,
  openCertificate,
  openResume,
}: IPharmacistProfileParams) => {
  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

  const {
    phone,
    firstName,
    lastName,
    licenseNumber,
    graduationYear,
    yearsOfExperience,
    remoteRegion,
    includeShiftContract,
    otherLanguages,
    city,
    province,
    address,
    postalCode,
    mobile,
    availabilityReminder,
    preferredWorkingDays,
    preferredReplacementDuration,
    certificateFileName,
    certificateUrl = '',
    selectedSoftwares,
    frenchLevel,
    englishLevel,
    incorporated,
    prefferedDistanceRange,
    userType,
    diploma,
    instituteName,
    techGraduationYear,
    entitlement,
    sendBookingInvitationReminder,
  } = details ?? {};

  const {
    leadEmail,
    leadName,
    campaignName,
    broadcastGroupId,
    broadcastGroupName,
    promoHourlyRate,
    totalCredits,
    creditsUsed,
    validUntil,
  } = entitlement ?? {};

  const isTechnician = `${userType}` === HelpTypes.technicians;

  const validUntilFormat = goToFallback(
    validUntil && getDayMonthDateYear(validUntil),
    fallbackText,
  );

  const {
    branchNumber,
    institutionNumber,
    accountNumber,
    corporateProvince,
    corporateCity,
    corporateCompanyName,
    corporatePhone,
    corporatePostalCode,
    corporateStreetAddress,
    tps,
    tvq,
  } = (details as IAdminProfessionalDetails) ?? {};

  const incorporatedInfo = [
    {
      key: 1,
      label: strings.companyName,
      value: corporateCompanyName,
    },
    {
      key: 2,
      label: strings.streetAddress,
      value: corporateStreetAddress,
    },
    {
      key: 3,
      label: strings.city,
      value: corporateCity,
    },
    {
      key: 4,
      label: strings.province,
      value: corporateProvince,
    },
    {
      key: 5,
      label: strings.postalCode,
      value: corporatePostalCode,
    },
    {
      key: 6,
      label: strings.telephoneNumber,
      value: corporatePhone,
    },
    {
      key: 7,
      label: strings.tpsNumber,
      value: goToFallback(tps, fallbackText),
    },
    {
      key: 8,
      label: strings.tvqNumber,
      value: goToFallback(tvq, fallbackText),
    },
  ];

  const nonIncorporatedInfo = [
    {
      key: 9,
      label: strings.tpsNumber,
      value: goToFallback(tps, fallbackText),
    },
    {
      key: 10,
      label: strings.tvqNumber,
      value: goToFallback(tvq, fallbackText),
    },
  ];

  const preferencesConfig = [
    {
      key: 0,
      label: strings.preferredWorkingDays,
      value: handleValue(handlePreferredDays(preferredWorkingDays as string)),
    },
    {
      key: 1,
      label: strings.preferredReplacementDuration,
      value: handleValue(preferredReplacementDuration),
    },
    {
      key: 2,
      label: strings.maxTravelDistance,
      value: `${handleSuffix(prefferedDistanceRange, 'Km')}`,
    },
    {
      key: 3,
      label: strings.remoteRegions,
      value: handleBooleanValue(remoteRegion),
    },
    {
      key: 4,
      label: strings.iAlsoWantToRequestMulti,
      value: handleBooleanValue(includeShiftContract),
    },
    {
      key: 5,
      label: strings.bookingInvitationEmails,
      value: handleBooleanValue(sendBookingInvitationReminder),
    },
  ];

  const infoForm = incorporated ? incorporatedInfo : nonIncorporatedInfo;

  if (!isSelfService) {
    preferencesConfig.push({
      key: 2,
      label: strings.availabilityReminder,
      value: handleBooleanValue(availabilityReminder),
    });
  }

  let professionalArr = [
    {
      key: professionalSelfServiceKeys.licenseKey,
      label: strings.licenseNumber,
      value: handleValue(licenseNumber),
    },

    {
      key: professionalSelfServiceKeys.technicianDiploma,
      label: strings.diploma,
      value: handleValue(diploma),
    },
    {
      key: professionalSelfServiceKeys.technicianInstitute,
      label: strings.educationalInstitute,
      value: handleValue(instituteName),
    },
    {
      key: professionalSelfServiceKeys.technicianGraduationYear,
      label: strings.graduationYear,
      value: handleValue(techGraduationYear),
    },
    resumeName && {
      key: 10,
      label: strings.formatString(
        strings.userCVFormat,
        UserLabels()[userType ?? HelpTypes.pharmacist],
      ),
      value: resumeName,
      customOpen: openResume,
    },
    certificateFileName && {
      key: professionalSelfServiceKeys.technicianGraduationYear,
      label: strings.diplomaCertificate,
      value: handleValue(certificateFileName),
      customOpen: openCertificate(certificateUrl),
    },
    {
      key: 1,
      label: strings.professionalCapacity,
      value: handleValue(pharmacistCapacity),
    },
    {
      key: professionalSelfServiceKeys.graduationYear,
      label: isTechnician ? strings.techExperienceSince : strings.yearLicensed,
      value: handleValue(graduationYear),
    },
    {
      key: professionalSelfServiceKeys.yearsOfExperience,
      label: strings.yearsOfExperience,
      value: handleValue(yearsOfExperience),
    },
    {
      key: 4,
      label: strings.englishLevel,
      value: languageLevelMap()[parseInt(englishLevel as string, 10)],
    },
    {
      key: 5,
      label: strings.frenchLevel,
      value: languageLevelMap()[parseInt(frenchLevel as string, 10)],
    },
    {
      key: 6,
      label: strings.otherLanguage,
      value: handleValue(otherLanguages),
    },
    {
      key: 7,
      label: strings.areYouIncorporated,
      value: handleBooleanValue(incorporated),
    },
  ].filter(Boolean) as Array<{
    key: number | string;
    label: string;
    value: string;
  }>;

  const keysToRemove = [
    professionalSelfServiceKeys.licenseKey,
    professionalSelfServiceKeys.yearsOfExperience,
    professionalSelfServiceKeys.graduationYear,
  ];
  const technicianKeys = [
    professionalSelfServiceKeys.technicianDiploma,
    professionalSelfServiceKeys.technicianInstitute,
    professionalSelfServiceKeys.technicianGraduationYear,
  ];
  const roleKeysToRemove: Record<string, Array<string | number>> = {
    [HelpTypes.students]: [...keysToRemove, ...technicianKeys],
    [HelpTypes.technicians]: [professionalSelfServiceKeys.licenseKey],
  };
  const professionalKeysToRemove = roleKeysToRemove[userType as keyof typeof roleKeysToRemove] ?? technicianKeys;

  professionalArr = professionalArr.filter((item) =>
    !professionalKeysToRemove.includes(item.key));

  let arr = [
    {
      key: 0,
      title: strings.general,
      keepExpanded: true,
      cardContent: [
        {
          key: 1,
          label: strings.firstName,
          value: handleValue(firstName),
        },
        {
          key: 2,
          label: strings.lastName,
          value: handleValue(lastName),
        },
        {
          key: 3,
          label: strings.preferredLanguage,
          value: handleValue(language),
        },
        {
          key: 4,
          label: strings.contactPreferences,
          value: handleValue(selectedPreferences),
        },
      ],
    },
    {
      key: 1,
      title: strings.professionalInformation,
      cardContent: professionalArr,
    },
    {
      key: professionalSelfServiceKeys.incorporated,
      title: strings.incorporated,
      cardContent: infoForm,
    },
    {
      key: professionalSelfServiceKeys.billing,
      title: strings.billing,
      cardContent: [
        {
          key: 1,
          label: strings.institutionNo,
          value: handleValue(institutionNumber),
        },
        {
          key: 2,
          label: strings.branchNo,
          value: handleValue(branchNumber),
        },
        {
          key: 3,
          label: strings.accountNo,
          value: goToFallback(
            accountNumber && handleValue('*'.repeat(5).concat(accountNumber)),
            fallbackText,
          ),
        },
      ],
    },
    {
      key: 2,
      title: strings.softwareFamiliarity,
      customComponent: (
        <SoftwareExpertise
          successAction={successAction}
          softwareDetails={selectedSoftwares}
          layout={TypeOfSoftwareExpertise.softwareFamilarity}
        />
      ),
    },
    {
      key: 3,
      title: strings.contactDetails,
      cardContent: [
        {
          key: 0,
          label: strings.address,
          value: handleValue(address),
        },
        {
          key: 1,
          label: strings.city,
          value: handleValue(city),
        },
        {
          key: 2,
          label: strings.province,
          value: handleValue(province),
        },
        {
          key: 3,
          label: strings.postalCode,
          value: handleValue(postalCode),
        },
        {
          key: 4,
          label: strings.homePhone,
          value: handleValue(phone),
        },
        {
          key: 5,
          label: strings.mobile,
          value: handleValue(mobile),
        },
      ],
    },
    {
      key: 4,
      title: strings.preferences,
      cardContent: preferencesConfig,
    },
    {
      key: campaignKey,
      title: strings.campaignDetails,
      cardContent: [
        {
          key: 1,
          label: strings.leadName,
          value: handleValue(leadName),
        },
        {
          key: 2,
          label: strings.leadEmail,
          value: handleValue(leadEmail),
        },
        {
          key: 3,
          label: strings.campaignSource,
          value: handleValue(campaignName),
        },
        broadcastGroupId && {
          key: 4,
          label: strings.leadSource,
          value: formatLeadSource(broadcastGroupName),
        },
        {
          key: 5,
          label: strings.promoHourlyBonus,
          value: convertDecimal(promoHourlyRate ?? 0),
        },
        {
          key: 6,
          label: strings.totalCredits,
          value: handleValue(totalCredits),
        },
        {
          key: 7,
          label: strings.creditsUsed,
          value: handleValue(creditsUsed),
        },
        {
          key: 8,
          label: strings.creditsValidUntil,
          value: validUntilFormat,
        },
      ].filter(Boolean) as Array<{ key: number; label: string; value: any }>,
    },
  ];

  if (isSelfService || !entitlement) {
    arr = arr.filter((item) =>
      item.key !== campaignKey);
  }

  if (!isSelfService) {
    arr = arr.filter(
      (item) =>
        !Object.values(professionalSelfServiceKeys).includes(
          item.key as string,
        ),
    );
  }

  if (fromPendingActivations) {
    arr = arr.filter(
      (item) =>
        item.key !== professionalSelfServiceKeys.billing,
    );
  }
  return arr;
};
